import { useQuery } from '@apollo/client/react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { Divider } from 'common/components/Divider';
import { InfoRow } from 'common/components/InfoRow';
import { Text } from 'common/components/Text';
import { LinearProgress } from 'common/components/LinearProgress';
import {
    formatDate,
    timeDeltaToDuration,
    durationToHuman,
} from 'common/utils/formatDate';
import {
    IContentTypeValue,
    IPortfolioItemFragment,
    IRole,
    CurrentUserDocument,
} from 'graphql/types';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { Link } from 'common/components/Link';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { isSkillPortfolioItem } from 'user/utils/portfolioItem';
import { ScoreInfoRow } from 'common/components/ScoreInfoRow';
import { PortfolioItemSyncInfoRows } from 'user/components/PortfolioItemSyncInfoRows';

interface IProps {
    hasMandatoryCertificates?: boolean;
    loading?: boolean;
    portfolioItem: IPortfolioItemFragment;
    relatedObjectDescription?: string;
    showDescription?: boolean;
    userId?: string;
}

export const PortfolioItemDetailsView = ({
    hasMandatoryCertificates,
    loading,
    portfolioItem,
    relatedObjectDescription,
    showDescription,
    userId,
}: IProps) => {
    const [translate] = useTranslation();
    const { data: currentUserData } = useQuery(CurrentUserDocument);
    const portfolioItemViewProfile = !!useRouteMatch('PORTFOLIO_ITEM_VIEW');
    const { openGlobalDrawer, closeGlobalDrawer } = useGlobalDrawer();

    const { currentUser } = currentUserData || {};
    const currentUserIsOwner =
        portfolioItemViewProfile ||
        (!portfolioItemViewProfile && userId === currentUser?.id);
    const currentUserIsManager = currentUser?.roles?.includes(IRole.Manager);

    const {
        title,
        startDate,
        completionDate,
        endDate,
        metadata,
        teacher,
        scoring,
        description,
        score,
        timeSpent,
        sessions,
        progress,
        scoreThreshold,
        relatedObject,
        relatedType,
        syncData,
    } = portfolioItem || {};

    const { courseVersionNumber, invitationSentAt, addedToTrainingVia } =
        metadata || {};

    const { groups, offerEvents, directly } = addedToTrainingVia || {};

    let addedVia = directly
        ? [<Box key="directly">{translate('addedDirectly')}</Box>]
        : [];

    const handleClick = (drawerType: IGlobalDrawerType, itemId: string) => {
        if (!currentUserIsManager) return;

        openGlobalDrawer({
            type: drawerType,
            itemId,
            props: {
                onClose: () =>
                    closeGlobalDrawer({
                        type: drawerType,
                    }),
            },
        });
    };

    if (!!groups?.length) {
        addedVia = [
            ...addedVia,
            ...groups.map((group, index) => (
                <Box
                    component={currentUserIsManager ? Link : 'div'}
                    key={group.id}
                    sx={{
                        display: currentUserIsManager ? 'block' : undefined,
                        cursor: currentUserIsManager ? 'pointer' : undefined,
                        mt: !addedVia.length && index === 0 ? 0 : 0.5,

                        '&:focus-visible': {
                            outlineOffset: '-2px',
                        },
                    }}
                    tabIndex={0}
                    onClick={() =>
                        handleClick(IGlobalDrawerType.Group, group.id)
                    }
                    onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                        if (e.key !== 'Enter') return;

                        handleClick(IGlobalDrawerType.Group, group.id);

                        e.preventDefault();
                    }}
                >
                    {group.name}
                </Box>
            )),
        ];
    }

    if (!!offerEvents?.length) {
        addedVia = [
            ...addedVia,
            ...offerEvents
                .filter(({ offer }) => !!offer)
                .map(({ id, offer }, index) => (
                    <Box
                        component={currentUserIsManager ? Link : 'div'}
                        key={offer.id}
                        sx={{
                            display: currentUserIsManager ? 'block' : undefined,
                            cursor: currentUserIsManager
                                ? 'pointer'
                                : undefined,
                            mt: !addedVia.length && index === 0 ? 0 : 0.5,

                            '&:focus-visible': {
                                outlineOffset: '-2px',
                            },
                        }}
                        tabIndex={0}
                        onClick={() =>
                            handleClick(IGlobalDrawerType.OfferEvent, id)
                        }
                        onKeyDown={(
                            e: React.KeyboardEvent<HTMLButtonElement>
                        ) => {
                            if (e.key !== 'Enter') return;

                            handleClick(IGlobalDrawerType.OfferEvent, id);

                            e.preventDefault();
                        }}
                    >
                        {offer.title}
                    </Box>
                )),
        ];
    }

    const timeSpentDuration =
        timeSpent && timeSpent.totalSeconds !== 0
            ? timeDeltaToDuration(timeSpent).shiftTo(
                  'days',
                  'hours',
                  'minutes',
                  'seconds'
              )
            : undefined;

    const showScore = progress === 1 && !!score;

    const isCertificate = relatedType === IContentTypeValue.ReceivedCertificate;

    const dateFormat = isCertificate ? 'dd MMMM yyyy' : 'dd MMMM yyyy HH:mm';

    const startDateDisplay = startDate ? formatDate(startDate, dateFormat) : '';

    // Show end date if the end date is in the present or future and progress is 1
    const endDateDisplay =
        !!endDate &&
        new Date(endDate) >= new Date() &&
        (isCertificate || progress === 1)
            ? formatDate(endDate, dateFormat)
            : '';
    const completionDateDisplay = completionDate
        ? formatDate(completionDate, 'dd MMMM yyyy HH:mm')
        : '';

    const scores = scoring?.map((scoreItem) => {
        const {
            score,
            scoringSystem: { nameSingular, namePlural },
        } = scoreItem;

        return {
            score,
            nameSingular,
            namePlural,
        };
    });

    const startDateTransKey = isCertificate ? 'portfolioStartDate' : 'start';

    const showProgress =
        progress !== undefined &&
        progress !== null &&
        (progress !== 1 || !completionDate) &&
        !isCertificate;

    const isSkillType = isSkillPortfolioItem(relatedObject, relatedType);

    return (
        <>
            <Box sx={{ p: 4 }}>
                <Typography sx={{ mb: 3 }} variant="h2">
                    {title}
                </Typography>

                <Stack spacing={2}>
                    {showScore && !isSkillType && (
                        <ScoreInfoRow
                            score={score}
                            scoreThreshold={scoreThreshold}
                        />
                    )}

                    {!loading &&
                        (hasMandatoryCertificates ? (
                            <InfoRow description={translate('certified')}>
                                {!!portfolioItem?.isCompliant &&
                                    translate('yes')}
                                {portfolioItem?.isCompliant === false &&
                                    translate('no')}
                                {portfolioItem?.isCompliant == null && '-'}
                            </InfoRow>
                        ) : (
                            showProgress && (
                                <InfoRow description={translate('progress')}>
                                    <Box sx={{ maxWidth: '200px' }}>
                                        <LinearProgress
                                            withProgressText
                                            aria-label={translate('progress')}
                                            color="primary"
                                            value={progress * 100}
                                        />
                                    </Box>
                                </InfoRow>
                            )
                        ))}

                    {startDateDisplay && (
                        <InfoRow description={translate(startDateTransKey)}>
                            {startDateDisplay}
                        </InfoRow>
                    )}

                    {completionDateDisplay && (
                        <InfoRow description={translate('fulfilledOn')}>
                            {completionDateDisplay}
                        </InfoRow>
                    )}

                    {!!endDateDisplay && (
                        <InfoRow description={translate('portfolioEndDate')}>
                            {endDateDisplay}
                        </InfoRow>
                    )}

                    {courseVersionNumber && (
                        <InfoRow description={translate('courseVersion')}>
                            {courseVersionNumber}
                        </InfoRow>
                    )}

                    {invitationSentAt && (
                        <InfoRow description={translate('invitationSentAt')}>
                            {formatDate(invitationSentAt, 'dd MMMM yyyy HH:mm')}
                        </InfoRow>
                    )}

                    {!!addedVia.length && (
                        <InfoRow description={translate('addedVia')}>
                            {addedVia}
                        </InfoRow>
                    )}

                    {teacher && (
                        <InfoRow description={translate('by')}>
                            {teacher}
                        </InfoRow>
                    )}

                    {!!timeSpentDuration && (
                        <InfoRow description={translate('time')}>
                            {durationToHuman(timeSpentDuration)}
                        </InfoRow>
                    )}

                    {!!sessions?.length && (
                        <InfoRow description={translate('sessions')}>
                            <Stack spacing={1}>
                                {sessions.map((session, index) => (
                                    <Box key={index}>
                                        {formatDate(
                                            session.startDate,
                                            'd MMM yyyy HH:mm'
                                        )}{' '}
                                        -
                                        {formatDate(
                                            session.endDate,
                                            'd MMM yyyy HH:mm'
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        </InfoRow>
                    )}

                    {!!scoring?.length && (
                        <InfoRow description={translate('portfolioPoints')}>
                            {scores
                                .filter((scoreItem) => scoreItem.score)
                                .map((scoreItem, index) => {
                                    const { score, nameSingular, namePlural } =
                                        scoreItem;

                                    const name =
                                        score === 1 ? nameSingular : namePlural;

                                    return `${score} ${name}${
                                        index === scores.length - 1 ? '' : ', '
                                    }`;
                                })}
                        </InfoRow>
                    )}

                    {!!syncData?.length && (
                        <PortfolioItemSyncInfoRows
                            portfolioItemId={portfolioItem.id}
                            syncData={syncData}
                        />
                    )}
                </Stack>

                {!!relatedObjectDescription && (
                    <Box sx={{ pt: 3 }}>
                        <Text component="div">
                            <Box sx={{ lineHeight: '1.6' }}>
                                {relatedObjectDescription}
                            </Box>
                        </Text>
                    </Box>
                )}
            </Box>
            {showDescription &&
                !currentUserIsOwner &&
                description &&
                !relatedObjectDescription && (
                    <>
                        <Divider />

                        <Box sx={{ p: 4 }}>
                            <Text component="div">
                                <Box sx={{ lineHeight: '1.6' }}>
                                    {description}
                                </Box>
                            </Text>
                        </Box>
                    </>
                )}
        </>
    );
};
